/**
 * Color constants for the application
 * Centralized color definitions for easy maintenance and consistency
 */

// Default theme colors
export const DEFAULT_THEME_COLORS = {
  light: {
    primary: "#1976d2",
    secondary: "#dc004e",
    background: "#ffffff",
    paper: "#ffffff",
    backgroundShadow: "#f5f5f5",
    buttonPrimary: "rgba(25, 118, 210, 0.04)",
    text: {
      primary: "rgba(0, 0, 0, 0.87)",
      secondary: "rgba(0, 0, 0, 0.6)",
    },
  },
  dark: {
    primary: "#90caf9",
    secondary: "#f48fb1",
    background: "#181a1b",
    paper: "#181a1b",
    backgroundShadow: "#1e1e1e",
    buttonPrimary: "rgba(25, 118, 210, 0.04)",
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
  },
};

// Fallback colors (used when CSS variables are not available)
export const FALLBACK_COLORS = {
  light: {
    primary: "#5177FF",
    background: "#ffffff",
    backgroundPaper: "#ffffff",
    textPrimary: "rgba(0, 0, 0, 0.87)",
    textSecondary: "#7f7f7f",
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
  dark: {
    primary: "#90caf9",
    background: "#181a1b",
    backgroundPaper: "#181a1b",
    textPrimary: "#ffffff",
    textSecondary: "rgba(255, 255, 255, 0.7)",
    borderColor: "rgba(255, 255, 255, 0.12)",
  },
};

// Common UI colors
export const UI_COLORS = {
  white: "#ffffff",
  black: "#000000",
  error: "#dc3545",
  success: "#28a745",
  warning: "#ffc107",
  info: "#17a2b8",
  
  // Bootstrap colors
  bootstrap: {
    lightRgb: "248, 249, 250",
    darkRgb: "33, 37, 41",
    lightRgbDark: "255, 255, 255",
    darkRgbDark: "0, 0, 0",
    borderOpacity: "0.12",
    codeColor: "#d63384",
    codeColorDark: "#e83e8c",
  },
  
  // Border colors
  border: {
    light: "#ced4da",
    dark: "rgba(255, 255, 255, 0.12)",
    lightTranslucent: "rgba(0, 0, 0, 0.12)",
    darkTranslucent: "rgba(255, 255, 255, 0.12)",
  },
  
  // Background overlays
  overlay: {
    light: "rgba(0, 0, 0, 0.04)",
    dark: "rgba(255, 255, 255, 0.08)",
    lightHover: "rgba(0, 0, 0, 0.08)",
    darkHover: "rgba(255, 255, 255, 0.1)",
    lightTertiary: "rgba(0, 0, 0, 0.05)",
    darkTertiary: "rgba(255, 255, 255, 0.05)",
  },
  
  // Primary color RGB (default blue)
  primaryRgb: {
    default: "25, 118, 210",
    fallback: "81, 119, 255",
  },
  
  // Text colors
  text: {
    white: "#ffffff",
    secondaryLight: "#7f7f7f",
    gray: "#505050",
  },
  
  // Disabled/Inactive states
  disabled: {
    background: "#f2f2f2",
  },
  
  // Border colors (additional)
  borderAdditional: {
    lightGray: "#e0e0e0",
    bootstrap: "#ced4da",
    focus: "#80bdff",
  },
  
  // Focus/Active states
  focus: {
    shadow: "rgba(0,123,255,.25)",
  },
  
  // Chart colors
  chart: {
    primaryLight: "#64b5f6",
    primaryLightAlt: "#B1C2FE",
    primaryOverlay: "rgba(81, 119, 255, 0.05)",
    primary: "#5177FF",
  },
};

// Theme preset colors
export const THEME_PRESET_COLORS = {
  light: {
    mode: "light",
    primaryColor: "#1976d2",
    secondaryColor: "#dc004e",
  },
  dark: {
    mode: "dark",
    primaryColor: "rgb(21, 101, 192)",
    secondaryColor: "#f48fb1",
  },
  blue: {
    mode: "light",
    primaryColor: "#1976d2",
    secondaryColor: "#42a5f5",
  },
  green: {
    mode: "light",
    primaryColor: "#2e7d32",
    secondaryColor: "#66bb6a",
  },
  purple: {
    mode: "light",
    primaryColor: "#7b1fa2",
    secondaryColor: "#ba68c8",
  },
  orange: {
    mode: "light",
    primaryColor: "#f57c00",
    secondaryColor: "#ffb74d",
  },
};

  // React Select specific colors
export const REACT_SELECT_COLORS = {
  border: {
    light: "#ced4da",
    dark: "rgba(255, 255, 255, 0.12)",
  },
  background: {
    light: "#ffffff",
    dark: null, // Uses theme.palette.background.paper
  },
  hover: {
    light: "rgba(0, 0, 0, 0.04)",
    dark: "rgba(255, 255, 255, 0.08)",
  },
  selectedText: "#ffffff",
  error: "#dc3545",
  primaryLight: "rgba(81, 119, 255, 0.25)",
  neutral: {
    light: {
      neutral5: "rgba(0, 0, 0, 0.05)",
      neutral8: "rgba(0, 0, 0, 0.08)", // For multiValue background
      neutral10: "rgba(0, 0, 0, 0.1)",
      neutral20: "#ced4da",
      neutral30: "#adb5bd",
      neutral40: "#868e96",
      neutral50: "#6c757d",
      neutral60: "#495057",
      neutral70: "#343a40",
      neutral80: "#212529",
      neutral90: "#0d1117",
    },
    dark: {
      neutral5: "rgba(255, 255, 255, 0.05)",
      neutral8: "rgba(255, 255, 255, 0.08)", // For multiValue background and primary25
      neutral10: "rgba(255, 255, 255, 0.1)",
      neutral20: "rgba(255, 255, 255, 0.12)",
      neutral30: "rgba(255, 255, 255, 0.2)",
      neutral40: "rgba(255, 255, 255, 0.3)",
      neutral50: "rgba(255, 255, 255, 0.5)",
      neutral60: "rgba(255, 255, 255, 0.6)",
      neutral70: "rgba(255, 255, 255, 0.7)",
      neutral80: "rgba(255, 255, 255, 0.8)",
      neutral90: "rgba(255, 255, 255, 0.9)",
    },
  },
};



// Threat Detection specific colors
export const THREAT_DETECTION_COLORS = {
  // Gradient colors for headers
  gradient: {
    primary: "#667eea",
    secondary: "#764ba2",
    hover: "#5a6fd8",
  },
  // Risk level colors
  riskLevel: {
    critical: "#dc3545",
    high: "#fd7e14",
    medium: "#ffc107",
    low: "#28a745",
    default: "#6c757d",
  },
  // Status colors
  status: {
    danger: "#e74a3b",
    warning: "#f6c23e",
    success: "#1cc88a",
    info: "#17a2b8",
  },
  // Dark mode status colors
  statusDark: {
    danger: "#ef5350",
    warning: "#ffb74d",
    success: "#1cc88a",
    info: "#90caf9",
  },
  // Text colors
  text: {
    primary: "#495057",
    secondary: "#5a5c69",
    muted: "#858796",
    gray: "#6c757d",
    dark: "#212529",
    infoDark: "#0c5460",
  },
  // Background colors
  background: {
    light: "#f8f9fa",
    gray: "#e9ecef",
    lightAlt: "#f8f9fc",
    infoLight: "#d1ecf1",
  },
  // Border colors
  border: {
    light: "#e3e6f0",
    gray: "#dee2e6",
  },
};
